import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useContacts } from '@/features/contacts/hooks/useContacts';
import type { Contact } from '@/features/contacts/types';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import type { VisitCoordinates, VisitDashboard, VisitFilters, VisitPurposeOption, VisitSummary } from '../types';

function visitListPath(filters: VisitFilters = {}) {
  const params = new URLSearchParams();
  if (filters.scope) params.set('scope', filters.scope);
  if (filters.date) params.set('date', filters.date);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.status) params.set('status', filters.status);
  const query = params.toString();
  return `/api/visits${query ? `?${query}` : ''}`;
}

export function useVisitList(filters: VisitFilters) {
  return useQuery({
    queryKey: ['visits', 'list', filters],
    queryFn: async () => {
      const { data } = await api.get<{ visits: VisitSummary[] }>(visitListPath(filters));
      return data.visits ?? [];
    },
  });
}

export function useVisitDashboard(date: string, scope: 'mine' | 'team' | 'all') {
  return useQuery({
    queryKey: ['visits', 'dashboard', date, scope],
    queryFn: async () => {
      const params = new URLSearchParams({ date, scope });
      const { data } = await api.get<{ dashboard: VisitDashboard }>(`/api/visits/dashboard?${params.toString()}`);
      return data.dashboard;
    },
  });
}

export function useVisitContacts() {
  const query = useContacts({});
  return { ...query, data: (query.data?.contacts ?? []) as Contact[] };
}

export function useVisitPurposes() {
  return useQuery({
    queryKey: ['visits', 'purposes'],
    queryFn: async () => {
      const { data } = await api.get<{ items: VisitPurposeOption[] }>('/api/masters/purpose');
      return data.items ?? [];
    },
  });
}

function invalidateVisits(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['visits'] });
  qc.invalidateQueries({ queryKey: ['followups'] });
}

export function useVisitMutations() {
  const qc = useQueryClient();
  return {
    createVisit: useMutation({
      mutationFn: async (payload: Record<string, unknown>) => (await api.post<{ visit: VisitSummary }>('/api/visits', payload)).data,
      onSuccess: () => invalidateVisits(qc),
    }),
    updateVisit: useMutation({
      mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) => (await api.patch<{ visit: VisitSummary }>(`/api/visits/${id}`, payload)).data,
      onSuccess: () => invalidateVisits(qc),
    }),
    checkIn: useMutation({
      mutationFn: async ({ id, coordinates }: { id: string; coordinates: VisitCoordinates }) => (await api.post<{ visit: VisitSummary }>(`/api/visits/${id}/check-in`, { coordinates })).data,
      onSuccess: () => invalidateVisits(qc),
    }),
    checkOut: useMutation({
      mutationFn: async ({ id, coordinates, visitReport, outcome, nextAction }: { id: string; coordinates: VisitCoordinates; visitReport?: string; outcome?: string; nextAction?: string }) => (
        await api.post<{ visit: VisitSummary }>(`/api/visits/${id}/check-out`, { coordinates, visitReport, outcome, nextAction })
      ).data,
      onSuccess: () => invalidateVisits(qc),
    }),
    cancelVisit: useMutation({
      mutationFn: async ({ id, outcome }: { id: string; outcome?: string }) => (await api.post<{ visit: VisitSummary }>(`/api/visits/${id}/cancel`, { outcome })).data,
      onSuccess: () => invalidateVisits(qc),
    }),
    convertToEnquiry: useMutation({
      mutationFn: async (id: string) => (await api.post<{ leadId: string }>(`/api/visits/${id}/convert/enquiry`)).data,
      onSuccess: () => invalidateVisits(qc),
    }),
    convertToFollowup: useMutation({
      mutationFn: async ({ id, followupNote, details, followupDate }: { id: string; followupNote?: string; details?: string; followupDate?: string }) => (
        await api.post<{ followupId: string }>(`/api/visits/${id}/convert/followup`, { followupNote, details, followupDate })
      ).data,
      onSuccess: () => invalidateVisits(qc),
    }),
  };
}

export function describeVisitError(error: unknown, fallback: string) {
  return getApiErrorMessage(error, fallback);
}

export type { Contact };
export { useUserOptions };
